import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { BellRing, CircleCheck, Sparkles } from 'lucide-react'
import { gsap, useGSAP } from '@/lib/gsap'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { RevealText } from '@/components/generals/reveal-text'
import { DashboardApp } from '@/components/dashboard/dashboard-app'

interface DashboardShowcaseProps {
    eyebrow?: string
    title?: string
    description?: string
    link?: boolean
}

export const DashboardShowcase: FC<DashboardShowcaseProps> = ({
    eyebrow = 'The product',
    title = 'A financial command center that runs itself.',
    description = 'Income, spend, cash and forecasts in one place, reconciled automatically and explained by AI. Switch the period to see every number update.',
    link = true
}): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const boardRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const section = sectionRef.current
        const board = boardRef.current

        if (!section || !board) return

        const media = gsap.matchMedia()

        media.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
            gsap.fromTo(
                board,
                { rotationX: 22, scale: 0.9, y: 60, transformPerspective: 2200, transformOrigin: '50% 0%' },
                {
                    rotationX: 0,
                    scale: 1,
                    y: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: board,
                        start: 'top 92%',
                        end: 'top 22%',
                        scrub: 0.8
                    }
                }
            )

            section.querySelectorAll<HTMLElement>('[data-float-depth]').forEach(element => {
                const depth = Number(element.dataset.floatDepth ?? 1)

                gsap.fromTo(
                    element,
                    { y: 90 * depth },
                    {
                        y: -90 * depth,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: board,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: 0.6
                        }
                    }
                )
            })
        })

        return () => media.revert()
    }, { scope: sectionRef })

    return (
        <section
            ref={sectionRef}
            id='dashboard'
            aria-labelledby='dashboard-title'
            className='relative overflow-hidden bg-burgundy text-cream'
        >
            <div aria-hidden='true' className='pointer-events-none absolute left-1/2 top-[38%] h-[640px] w-[1100px] -translate-x-1/2 rounded-full bg-burgundy-soft blur-[120px]' />

            <Container className='relative pt-28 md:pt-36'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <Eyebrow tone='light'>{eyebrow}</Eyebrow>

                        <RevealText
                            as='h2'
                            id='dashboard-title'
                            text={title}
                            highlight={['runs itself.']}
                            highlightClassName='text-beige'
                            className='mt-6 font-heading text-display-xl font-medium'
                        />
                    </div>

                    <div data-reveal='' className='lg:col-span-4 lg:col-start-9'>
                        <p className='text-lg leading-relaxed text-cream/70'>{description}</p>

                        {link && (
                            <ButtonLink href='/product' variant='light' className='mt-8' arrow>
                                Explore the product
                            </ButtonLink>
                        )}
                    </div>
                </div>
            </Container>

            <Container className='relative pb-28 pt-16 md:pb-40 md:pt-20'>
                <div className='relative'>
                    <div ref={boardRef} className='will-change-transform'>
                        <DashboardApp />
                    </div>

                    <div
                        aria-hidden='true'
                        data-float-depth='1'
                        className='pointer-events-none absolute -right-4 -top-10 z-10 hidden w-64 rounded-2xl border border-cream/10 bg-burgundy-soft/95 p-4 text-cream shadow-[0_40px_80px_-30px_rgb(0_0_0/0.6)] backdrop-blur lg:block xl:-right-10'
                    >
                        <p className='flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-beige'>
                            <Sparkles className='size-3.5 text-gold' />
                            AI insight
                        </p>

                        <p className='mt-2 text-[13px] leading-relaxed text-cream/90'>
                            Enterprise customers are paying 6 days faster, adding $310K to projected Q4 cash.
                        </p>
                    </div>

                    <div
                        aria-hidden='true'
                        data-float-depth='0.6'
                        className='pointer-events-none absolute -left-6 top-[38%] z-10 hidden w-60 rounded-2xl border border-burgundy/10 bg-ivory/95 p-3.5 text-burgundy shadow-[0_40px_80px_-30px_rgb(0_0_0/0.6)] backdrop-blur lg:block xl:-left-12'
                    >
                        <p className='flex items-center gap-2 text-xs font-medium'>
                            <span className='flex size-6 items-center justify-center rounded-full bg-sage/25 text-sage-dark'>
                                <CircleCheck className='size-3.5' />
                            </span>
                            Payroll reconciled
                        </p>

                        <p className='mt-1.5 text-[11px] text-stone'>September payroll matched to 3 bank transfers</p>
                    </div>

                    <div
                        aria-hidden='true'
                        data-float-depth='1.3'
                        className='pointer-events-none absolute -bottom-8 right-[18%] z-10 hidden w-56 rounded-2xl border border-burgundy/10 bg-ivory/95 p-3.5 text-burgundy shadow-[0_40px_80px_-30px_rgb(0_0_0/0.6)] backdrop-blur lg:block'
                    >
                        <p className='flex items-center gap-2 text-xs font-medium'>
                            <span className='flex size-6 items-center justify-center rounded-full bg-gold/25 text-gold-dark'>
                                <BellRing className='size-3.5' />
                            </span>
                            Budget alert
                        </p>

                        <p className='mt-1.5 text-[11px] text-stone'>Marketing is pacing 9% over its Q4 plan</p>
                    </div>
                </div>
            </Container>
        </section>
    )
}
